/**
 * /admin — Admin landing. Admin-only back-office configuration hub.
 *
 * Created 2026-06-21 (nav reorg, Pedro): a dedicated Admin sidebar entry that
 * separates back-office configuration from compliance review. Houses the cards
 * that previously lived on the Compliance overview (Users / Permissions / SAA /
 * Standard fees / Compute fees), plus a Portfolio Data hub and a Translations
 * card (Translations moved off the Compliance tabs).
 *
 * Access: admin-only (Pedro 2026-06-21). FOLLOW-UP logged: turn the Admin-menu
 * visibility into a Permissions-framework affordance so non-admins can be granted
 * it per-user (notably to give Compliance back its SAA/Standard-fees/Translations
 * nav path). Until then the individual pages keep their own per-card gates.
 */
import { Container, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import {
  IconBriefcase, IconBucket, IconBuilding, IconBuildingBank, IconCalculator, IconChartLine, IconCoins, IconFileText, IconGavel,
  IconLanguage, IconPercentage, IconReceiptTax, IconReportAnalytics, IconScale, IconShieldLock,
  IconSitemap, IconTableExport, IconUserCog, IconUsers,
} from '@tabler/icons-react';

import { MetricCard } from '../components/MetricCard';
import { AccessDeniedAlert } from '../components/RequireAccess';
import { useCurrentUser } from '../hooks/useAuth';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { usePermissions } from '../hooks/usePermissions';

export function AdminPage() {
  useDocumentTitle('Admin');
  const { user } = useCurrentUser();
  const { can } = usePermissions();

  if (!user) return null;
  if (user.role !== 'admin') return <AccessDeniedAlert />;

  return (
    <Container size="xl">
      <Stack>
        <Title order={2}>Admin</Title>
        <Text size="sm" c="dimmed">
          Back-office configuration — portfolio data, users, permissions, reference
          data, fees, and translations.
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
          <MetricCard
            title="AuM & Returns"
            subtitle="EOM AuM, cash flows & returns"
            icon={IconChartLine}
            to="/admin/portfolio-data"
          />
          <MetricCard
            title="MIS"
            subtitle="AuM, revenue & run-rate across the book"
            icon={IconReportAnalytics}
            to="/reports"
          />
          <MetricCard
            title="Client Reports"
            subtitle="Publish monthly page-1 & produce client reports"
            icon={IconFileText}
            to="/admin/client-reports"
          />
          <MetricCard
            title="Users"
            subtitle="Accounts, roles, force-logout"
            icon={IconUserCog}
            to="/users"
          />
          <MetricCard
            title="Named RMs"
            subtitle="Assign relationship managers across agreements"
            icon={IconUsers}
            to="/admin/rms"
          />
          {can('user.manage_permissions') && (
            <MetricCard
              title="Permissions"
              subtitle="Group defaults + per-user overrides"
              icon={IconShieldLock}
              to="/admin/permissions"
            />
          )}
          {can('saa.manage') && (
            <MetricCard
              title="SAA reference"
              subtitle="Profiles, asset classes, allocations"
              icon={IconScale}
              to="/admin/saa"
            />
          )}
          <MetricCard
            title="Classification"
            subtitle="Securities-master taxonomy — lenses, tiers & values"
            icon={IconSitemap}
            to="/admin/classification"
          />
          <MetricCard
            title="Securities"
            subtitle="Securities master — search, classify & inspect instruments"
            icon={IconBuildingBank}
            to="/admin/securities"
          />
          <MetricCard
            title="Fees"
            subtitle="Compute month + invoice history + fee schedules"
            icon={IconCalculator}
            to="/admin/fees"
          />
          {can('portfolios.manage') && (
            <MetricCard
              title="Portfolios"
              subtitle="Upload the daily positions file; view each client portfolio"
              icon={IconBriefcase}
              to="/admin/portfolios?from=admin"
            />
          )}
          {/* Model Portfolios M1 (Pedro 2026-10-26: "separate Models card"), read by
              `portfolios.read`, changed by `model.manage`. */}
          {can('portfolios.read') && (
            <MetricCard
              title="Models"
              subtitle="Model portfolios: buckets of Approved securities and cash"
              icon={IconBucket}
              to="/admin/models?from=admin"
            />
          )}
          {/* TRADES-RM (Pedro 2026-10-23): no Trades card; trading is the Trade button on
              Portfolios → View portfolio, for whoever holds `trades.draft`. */}
          <MetricCard
            title="Banks"
            subtitle="Mnemonic, name, address, SWIFT and contacts of each bank"
            icon={IconBuilding}
            to="/admin/banks"
          />
          {can('capital_gains.run') && (
            <MetricCard
              title="Capital gains"
              subtitle="FIFO lots + capital-gains tax from custodian exports"
              icon={IconReceiptTax}
              to="/admin/capital-gains"
            />
          )}
          <MetricCard
            title="Data exports"
            subtitle="Full lists as Excel: agreements, persons, accounts, policies"
            icon={IconTableExport}
            to="/admin/data-exports"
          />
          <MetricCard
            title="RM commissions"
            subtitle="Commission tables per RM; monthly commission on billed fees"
            icon={IconPercentage}
            to="/admin/commissions"
          />
          <MetricCard
            title="Translations"
            subtitle="IAA-generator translation registry"
            icon={IconLanguage}
            to="/admin/translations"
          />
          <MetricCard
            title="Firm financials"
            subtitle="Firm-level financials (coming soon)"
            icon={IconCoins}
            to="/admin/firm-financials"
          />
          <MetricCard
            title="Regulatory"
            subtitle="Regulatory content (coming soon)"
            icon={IconGavel}
            to="/admin/regulatory"
          />
        </SimpleGrid>
      </Stack>
    </Container>
  );
}
